import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { StepFlow } from "@/components/StepFlow";
import { CountryPicker } from "@/components/CountryPicker";
import { ArrowsClockwise, Check, PaperPlaneRight } from "@phosphor-icons/react";

export default function TopUp() {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [recipient, setRecipient] = useState("");

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem("iyeneh_buy_intent");
      if (raw) { const i = JSON.parse(raw); if (i.recipient) setRecipient(i.recipient); localStorage.removeItem("iyeneh_buy_intent"); }
    } catch {}
  }, []);
  const [country, setCountry] = useState("CM");
  const [operator, setOperator] = useState(null);
  const [product, setProduct] = useState(null);
  const [busy, setBusy] = useState(false);
  const [freq, setFreq] = useState("once");
  const [ptype, setPtype] = useState("All");
  const [global, setGlobal] = useState(false);
  const [opSearch, setOpSearch] = useState("");

  const { data: operators, isFetching: opsLoading } = useQuery({
    queryKey: ["topup-ops", global ? "ALL" : country],
    queryFn: async () => (await api.get(`/topup/operators?country=${global ? "ALL" : country}`)).data,
    enabled: step >= 1,
    staleTime: 600000,
  });
  const { data: products, isFetching: prodLoading, isError: prodError } = useQuery({
    queryKey: ["topup-products", operator?.id],
    queryFn: async () => (await api.get(`/topup/products?operator=${encodeURIComponent(operator.id)}&country=${operator.country || country}`)).data,
    enabled: !!operator && step === 1,
  });
  const { data: cmOps } = useQuery({
    queryKey: ["topup-ops-cm"],
    queryFn: async () => (await api.get("/topup/operators?country=CM")).data,
    enabled: country === "CM",
  });
  const { data: history } = useQuery({ queryKey: ["topup-history"], queryFn: async () => (await api.get("/topup/history")).data });
  const { data: schedules } = useQuery({ queryKey: ["topup-schedules"], queryFn: async () => (await api.get("/topup/schedules")).data });

  const send = async () => {
    setBusy(true);
    const ctry = operator.country || country;
    try {
      if (freq !== "once") {
        await api.post("/topup/schedules", {
          recipient, operator: operator.id, operator_name: operator.name, country: ctry,
          product_id: product.id, label: product.label, retail_price_cents: product.retail_price_cents,
          frequency: freq,
        });
        toast.success(`Recurring top-up scheduled (${freq})`);
        qc.invalidateQueries({ queryKey: ["topup-schedules"] });
      } else {
        const res = await api.post("/topup/send", {
          recipient, operator: operator.id, operator_name: operator.name, country: ctry, product_id: product.id,
          retail_price_cents: product.retail_price_cents, label: product.label,
        });
        toast.success(res.data?.note || "Top up sent");
        qc.invalidateQueries({ queryKey: ["wallet"] });
        qc.invalidateQueries({ queryKey: ["topup-history"] });
      }
      setStep(0); setProduct(null); setOperator(null); setRecipient(""); setFreq("once");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const cancelSchedule = async (id) => {
    try { await api.delete(`/topup/schedules/${id}`); qc.invalidateQueries({ queryKey: ["topup-schedules"] }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Mobile Top Up</h1>
        <p className="mt-2 text-muted-foreground">Number → Product → Send. Instant airtime &amp; data, worldwide.</p>
      </div>
      <StepFlow steps={["Number", "Product", "Send"]} current={step} />

      {step === 0 && (
        <div className="animate-fade-up relative z-30 mx-auto max-w-md rounded-3xl border border-border bg-card p-6">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recipient country</label>
          <div className="mt-2">
            <CountryPicker value={country} onChange={(c) => { setCountry(c.code); setRecipient(c.dial); }} />
          </div>
          <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Mobile number</label>
          <input data-testid="topup-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="+1 555 010 2020"
            className="mt-2 w-full rounded-xl border border-input bg-card px-4 py-3 font-mono-metric outline-none focus:border-primary" />
          <button data-testid="topup-next" disabled={!recipient} onClick={() => setStep(1)}
            className="mt-6 w-full rounded-full bg-primary py-3.5 font-semibold text-primary-foreground disabled:opacity-50">Continue</button>
        </div>
      )}

      {step === 0 && country === "CM" && (cmOps?.operators?.length > 0) && (
        <div data-testid="cm-quickpick" className="animate-fade-up relative z-10 mx-auto max-w-md rounded-3xl border border-primary/30 bg-accent/40 p-5">
          <p className="flex items-center gap-2 text-sm font-semibold"><img src="https://flagcdn.com/cm.svg" alt="" className="h-4 w-6 rounded-sm object-cover ring-1 ring-black/5" /> Popular in Cameroon</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Send home in two taps — enter the number above, then tap a network to see amounts.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {cmOps.operators.map((o) => (
              <button key={o.id} data-testid={`cm-quick-${o.id}`}
                disabled={recipient.trim().length < 6}
                onClick={() => { setOperator(o); setStep(1); }}
                className="rounded-xl border border-border bg-card px-3 py-3 text-sm font-medium transition-colors hover:border-primary disabled:opacity-50">
                {o.name}
              </button>
            ))}
          </div>
          {recipient.trim().length < 6 && <p className="mt-2 text-xs text-amber-600">Enter the recipient number above first.</p>}
        </div>
      )}

      {step === 1 && (
        <div className="animate-fade-up space-y-4">
          <button onClick={() => setStep(0)} className="rounded-full border border-border px-3 py-1.5 text-sm">← {recipient}</button>
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Operator {global ? "· all countries" : `· ${country}`}</p>
              <button data-testid="topup-global-toggle" onClick={() => { setGlobal(!global); setOperator(null); setOpSearch(""); }}
                className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:border-primary">
                {global ? `Only ${country}` : "Browse all countries"}
              </button>
            </div>
            {global && (
              <input data-testid="topup-operator-search" value={opSearch} onChange={(e) => setOpSearch(e.target.value)}
                placeholder="Search operator or country…"
                className="mb-2 w-full rounded-xl border border-input bg-card px-4 py-2.5 text-sm outline-none focus:border-primary" />
            )}
            {opsLoading && <p className="text-sm text-muted-foreground">Loading operators…</p>}
            <div className="flex max-h-72 flex-wrap gap-2 overflow-y-auto">
              {(operators?.operators || [])
                .filter((o) => !opSearch || `${o.name} ${o.country_name || ""}`.toLowerCase().includes(opSearch.toLowerCase()))
                .slice(0, global ? 80 : 200)
                .map((o) => (
                <button key={o.id} data-testid={`operator-${o.id}`} onClick={() => { setOperator(o); setPtype("All"); }}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${operator?.id === o.id ? "bg-primary text-primary-foreground" : "border border-border"}`}>
                  {o.name}{global && o.country_name ? <span className="ml-1 text-xs opacity-70">· {o.country_name}</span> : null}
                </button>
              ))}
            </div>
          </div>
          {operator && products?.products?.length > 0 && (
            <div data-testid="topup-type-filter" className="flex flex-wrap gap-2">
              {[["All", "All"], ["Airtime", "Airtime"], ["Bundle", "Bundles"], ["Data", "Data only"]].map(([k, lbl]) => {
                const n = k === "All" ? products.products.length : products.products.filter((p) => p.type === k).length;
                return (
                  <button key={k} data-testid={`topup-type-${k.toLowerCase()}`} onClick={() => setPtype(k)} disabled={!n}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors disabled:opacity-40 ${ptype === k ? "bg-foreground text-background" : "border border-border"}`}>
                    {lbl} ({n})
                  </button>
                );
              })}
            </div>
          )}
          {operator && (
            prodLoading ? (
              <div className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">Loading products…</div>
            ) : prodError ? (
              <div data-testid="products-error" className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-600">Couldn't load products for this operator. Please try another.</div>
            ) : (products?.products?.length ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {products.products.filter((p) => ptype === "All" || p.type === ptype || (ptype === "Airtime" && p.type === "Custom")).map((p) => (
                  <button key={p.id} data-testid={`product-${p.id}`} onClick={() => { setProduct(p); setStep(2); }}
                    className="flex items-center justify-between rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-primary">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{p.type}</p>
                      <p className="font-medium">{p.label}</p>
                      {(p.benefits || p.validity) && <p className="text-xs text-muted-foreground">{[p.benefits, p.validity].filter(Boolean).join(" · ")}</p>}
                    </div>
                    <span className="font-mono-metric font-semibold">{money(p.retail_price_cents)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div data-testid="products-empty" className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">No products available for this operator.</div>
            ))
          )}
        </div>
      )}

      {step === 2 && product && (
        <div className="animate-fade-up mx-auto max-w-md rounded-3xl border border-border bg-card p-6">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-primary"><ArrowsClockwise size={28} weight="duotone" /></div>
          <h2 className="font-display text-xl font-semibold">Confirm top up</h2>
          <dl className="mt-4 space-y-3 text-sm">
            {[["Recipient", recipient], ["Operator", operator.name], ["Product", product.label]].map(([k, v]) => (
              <div key={k} className="flex justify-between"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium">{v}</dd></div>
            ))}
            <div className="flex justify-between border-t border-border pt-3"><dt className="font-semibold">Total</dt><dd className="font-mono-metric font-semibold">{money(product.retail_price_cents)}</dd></div>
          </dl>
          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Repeat</p>
            <div className="grid grid-cols-4 gap-2">
              {["once", "daily", "weekly", "monthly"].map((f) => (
                <button key={f} data-testid={`topup-freq-${f}`} onClick={() => setFreq(f)}
                  className={`relative rounded-xl border py-2 text-xs font-medium capitalize transition-colors ${freq === f ? "border-primary bg-accent" : "border-border"}`}>
                  {f}
                  {f === "monthly" && <span className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-primary-foreground">Popular</span>}
                </button>
              ))}
            </div>
            {freq === "once" && <p className="mt-2 text-xs text-primary">Sending every month? Tap <b>Monthly</b> to automate it — never forget again.</p>}
            {freq !== "once" && <p className="mt-2 text-xs text-muted-foreground">We'll auto-send this top-up {freq} from your wallet. Cancel anytime below.</p>}
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={() => setStep(1)} className="rounded-full border border-border px-5 py-3 text-sm font-medium">Back</button>
            <button data-testid="topup-send" onClick={send} disabled={busy}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.02] disabled:opacity-60">
              <PaperPlaneRight size={18} weight="bold" /> {busy ? "Working…" : (freq === "once" ? "Top Up Now" : `Schedule ${freq}`)}
            </button>
          </div>
        </div>
      )}

      {schedules?.schedules?.length > 0 && (
        <div>
          <h2 className="mb-3 mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recurring top-ups</h2>
          {schedules.insights && !schedules.insights.sufficient && (
            <div data-testid="schedule-low-balance" className="mb-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              ⚠ Your balance ({money(schedules.insights.balance_cents)}) is below the {money(schedules.insights.cycle_total_cents)} needed for your {schedules.insights.count} recurring top-up(s). Add funds so they don't skip.
            </div>
          )}
          <div className="space-y-2">
            {schedules.schedules.map((s) => (
              <div key={s.id} data-testid={`schedule-${s.id}`} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                <div><p className="font-mono-metric text-sm font-medium">{s.recipient}</p><p className="text-xs text-muted-foreground capitalize">{s.frequency} · {s.label} · {money(s.amount_cents)}{s.next_run ? ` · next ${shortDate(s.next_run)}` : ""}{s.last_error ? ` · ⚠ ${s.last_error}` : ""}</p></div>
                <button data-testid={`schedule-cancel-${s.id}`} onClick={() => cancelSchedule(s.id)} className="rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:border-destructive hover:text-destructive">Cancel</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {history?.topups?.length > 0 && (
        <div>
          <h2 className="mb-3 mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Recent top ups</h2>
          <div className="space-y-2">
            {history.topups.slice(0, 6).map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
                <div><p className="font-mono-metric text-sm font-medium">{t.recipient}</p><p className="text-xs text-muted-foreground">{t.operator} · {t.label} · {shortDate(t.created_at)}</p></div>
                <div className="text-right"><p className="font-mono-metric text-sm font-semibold">{money(t.amount_cents)}</p><p className="text-xs text-emerald-600">{t.status}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
